import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  TextField,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import apiFetch from "../../utils/apiFetch";
import { SCHOOL_YEAR_STORAGE_KEY } from "../../shared/schoolYear";
import { ExpandSlot, NAV_ICON_COLOR } from "./SchoolNavMenu";
import {
  NAV_CARET_ICON_SIZE,
  NAV_CARET_SLOT_SX,
  NAV_ROW_ACTIVE,
  NAV_ROW_ACTIVE_HOVER,
  NAV_ROW_SX,
  NAV_SURFACE_DIVIDER,
  NAV_SURFACE_HOVER,
} from "./navSurface";

// Clears the stored school year then reloads on the v2 chrome route, matching the
// legacy school switcher behavior. Selecting the current school is a no-op.
function switchToSchool(school, currentSchoolSlug) {
  if (school.slug === currentSchoolSlug) return;
  try {
    window.localStorage.removeItem(SCHOOL_YEAR_STORAGE_KEY);
  } catch (e) {
    // ignore storage errors (private mode, etc.)
  }
  window.location.replace(`/v2/school/${school.slug}`);
}

// The schools this staff member can switch to. It lives in a hook (rather than
// inside a component) because the collapsed trigger and the open panel now
// render in different regions of the sidebar - the fixed header and the
// scrollable body - and must not each fire the request.
export function useStaffSchools(staffId) {
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    if (!staffId) return undefined;
    let cancelled = false;
    apiFetch(`/api/staffs/${staffId}/schools`)
      .then((res) => (res.ok ? res.json() : null))
      .then((payload) => {
        if (cancelled) return;
        setSchools(payload?.data?.schools ?? []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [staffId]);

  return schools;
}

// The inline switcher panel: a client-side search field over the already-fetched
// schools plus the filterable list. It mounts only while the switcher is open, so
// the query resets and the search field re-focuses on each open for free.
export function SchoolSwitcherPanel({ schools, currentSchoolSlug }) {
  const [query, setQuery] = useState("");
  // Index of the keyboard-highlighted option. Focus stays in the search field
  // (combobox pattern); arrow keys move this highlight and Enter selects it.
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const itemRefs = useRef([]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return schools;
    return schools.filter((school) =>
      school.name.toLowerCase().includes(needle)
    );
  }, [schools, query]);

  // Reset the highlight to the top whenever the filtered set changes, so it never
  // points past the end of the list.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  // Keep the highlighted row in view as the user arrows through a long list.
  useEffect(() => {
    itemRefs.current[highlightedIndex]?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const handleKeyDown = (e) => {
    if (filtered.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const school = filtered[highlightedIndex];
      if (school) switchToSchool(school, currentSchoolSlug);
    }
  };

  const activeOptionId =
    filtered[highlightedIndex] &&
    `school-option-${filtered[highlightedIndex].id}`;

  return (
    <Box>
      <TextField
        autoFocus
        fullWidth
        size="small"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search schools"
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: NAV_ICON_COLOR }} />
              </InputAdornment>
            ),
            endAdornment: query ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setQuery("")}
                  edge="end"
                >
                  <ClearIcon fontSize="small" sx={{ color: NAV_ICON_COLOR }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
          htmlInput: {
            "aria-label": "Search schools",
            role: "combobox",
            "aria-expanded": true,
            "aria-controls": "school-switcher-list",
            "aria-activedescendant": activeOptionId || undefined,
          },
        }}
        sx={{
          mb: 1,
          // The field sits on the dark nav surface: white type inside a faint
          // white outline instead of the default dark-on-light input.
          "& .MuiOutlinedInput-root": {
            color: "#FFFFFF",
            "& fieldset": { borderColor: "rgba(255, 255, 255, 0.23)" },
            "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.4)" },
            "&.Mui-focused fieldset": { borderColor: "#FFFFFF" },
          },
          "& .MuiInputBase-input::placeholder": {
            color: NAV_ICON_COLOR,
            opacity: 1,
          },
        }}
      />
      {filtered.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: NAV_ICON_COLOR, px: 1, py: 1 }}>
          No schools found
        </Typography>
      ) : (
        <List id="school-switcher-list" role="listbox" sx={{ p: 0 }}>
          {filtered.map((school, index) => {
            const isActive = school.slug === currentSchoolSlug;
            const isHighlighted = index === highlightedIndex;
            const label = `${school.name}${
              school.is_default_school ? " (Default)" : ""
            }`;
            // The current school is marked the way an active nav item is - the
            // brighter wash plus a heavier label - so the two lists read alike.
            let background = "transparent";
            if (isActive) background = NAV_ROW_ACTIVE;
            else if (isHighlighted) background = NAV_SURFACE_HOVER;
            return (
              <ListItemButton
                key={school.id}
                id={`school-option-${school.id}`}
                role="option"
                aria-selected={isHighlighted}
                selected={isActive}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                onClick={() => switchToSchool(school, currentSchoolSlug)}
                onMouseEnter={() => setHighlightedIndex(index)}
                aria-label={`Switch to ${school.name}`}
                sx={{
                  ...NAV_ROW_SX,
                  backgroundColor: background,
                  "&.Mui-selected": { backgroundColor: NAV_ROW_ACTIVE },
                  "&:hover, &.Mui-selected:hover": {
                    backgroundColor: isActive
                      ? NAV_ROW_ACTIVE_HOVER
                      : NAV_SURFACE_HOVER,
                  },
                }}
              >
                {/* The checkmark rides in the caret column, so school names sit
                    in the same column as the trigger's label above and the glyph
                    matches a nav row's caret in position and size. */}
                <Box sx={NAV_CARET_SLOT_SX}>
                  {isActive && (
                    <CheckIcon
                      sx={{
                        fontSize: NAV_CARET_ICON_SIZE,
                        color: NAV_ICON_COLOR,
                      }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: 16,
                    fontWeight: isActive ? 500 : 400,
                    color: "#FFFFFF",
                  }}
                >
                  {label}
                </Typography>
              </ListItemButton>
            );
          })}
        </List>
      )}
    </Box>
  );
}

// Collapsed school switcher row, pinned in the sidebar's fixed header. It is a
// navigation row in every respect - same caret slot on the left, same row
// padding, same label size, its name in medium weight. Opening it is the
// parent's business: SchoolNavContents
// fades the menu out and renders SchoolSwitcherPanel in the scroll region below,
// so a long school list scrolls where the menu does. It closes with a full-width
// divider so the section reads as its own band.
export default function SchoolSwitcherTrigger({
  currentSchoolName,
  open,
  onToggle,
}) {
  return (
    <Box sx={{ px: 1, py: 0.5, borderBottom: NAV_SURFACE_DIVIDER }}>
      <ListItemButton
        role="button"
        aria-label="Switch school"
        aria-expanded={open}
        onClick={onToggle}
        sx={{
          ...NAV_ROW_SX,
          "&:hover": { backgroundColor: NAV_SURFACE_HOVER },
        }}
      >
        <ExpandSlot hasChildren open={open} onDark />
        <Typography
          sx={{
            flex: 1,
            fontSize: 16,
            fontWeight: 500,
            color: "#FFFFFF",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {currentSchoolName}
        </Typography>
      </ListItemButton>
    </Box>
  );
}
